/**
 * Shared "upload an ascending set of parts" loop — the ONE code path used by
 * both a fresh send's initial upload (sendFile.ts, right after the hash
 * pass) and a resumed send's server-reconciled upload (senderResume.ts),
 * per docs/file-transfer-resume.md §3.1.4 point 3: "pass 2 uploads always
 * read from the OPFS copy (never the original File), fresh session and
 * resumed session alike — one code path."
 *
 * Reads plaintext chunks for each GLOBAL part from the injected per-file `sourceFor`
 * (never the original `File` or an in-memory ciphertext buffer) — a `partPlan` entry
 * (docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md §4) maps each global
 * part to exactly one file's file-LOCAL chunk range (file-aligned: a part never straddles
 * a file), so a single-file transfer is the degenerate one-file case of the same loop.
 * Encrypts with the transfer's key and uploads with the M1 part-upload contract verbatim:
 * a fresh presigned `upload-url` per attempt -> PUT -> `uploaded`, `PART_RETRY_BACKOFF_MS`
 * budget (1 initial attempt + 3 retries). Throws `PartUploadError` when a part's retries
 * are exhausted — callers own the cleanup (error ctrl + DELETE + terminal transition,
 * docs/file-transfer-resume.md §5.2 step 5).
 *
 * `PART_RETRY_BACKOFF_MS`/`PartUploadError` live HERE (not `sendFile.ts`,
 * which re-exports them for backward-compat import paths) and every other
 * type is imported `type`-only from `./sendFile` (erased at compile time),
 * so this module has NO runtime dependency on `sendFile.ts` even though
 * `sendFile.ts` imports `runUploadLoop` from here at runtime — a one-way
 * edge, no circular module load.
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import { isAlreadyComplete, isDefinitive404 } from "./errors";
import { globalChunkOffsets, type PartPlanEntry } from "./partPlan";
import type { SenderCrypto, SenderSource, TransferApi } from "./sendFile";

/** Backoff delays BEFORE each retry (not before the initial attempt) — 1 initial attempt + 3 retries = 4 total attempts per part. */
export const PART_RETRY_BACKOFF_MS = [1000, 4000, 16000] as const;

export class PartUploadError extends Error {
  constructor(
    public readonly partIndex: number,
    public readonly cause?: unknown
  ) {
    super(`part ${partIndex} failed after exhausting retries`);
    this.name = "PartUploadError";
  }
}

/**
 * Thrown by `runUploadLoop` when `checkInterrupt` reports `"pause"` at the
 * per-part checkpoint (after a part's PUT + `uploaded` + P-S2 persist all
 * succeed — parts are atomic, never interrupted mid-part). Distinguishable
 * from `PartUploadError` on purpose: this is a user/peer-driven pause, not a
 * retry-exhaustion failure — callers (sendFile.ts/senderResume.ts) persist
 * `"paused"` and return normally, they do NOT treat this as a thrown failure.
 */
export class UploadPausedError extends Error {
  constructor(public readonly partIndex: number) {
    super(`upload paused after part ${partIndex}`);
    this.name = "UploadPausedError";
  }
}

/**
 * Thrown by `runUploadLoop` when `checkInterrupt` reports `"cancel"` at the
 * per-part checkpoint. Distinguishable from `PartUploadError` for the same
 * reason as `UploadPausedError` — callers own the cancel cleanup (best-effort
 * cancel ctrl on LOCAL origin only, `DELETE`, terminal `"cancelled"`).
 */
export class UploadCancelledError extends Error {
  constructor(public readonly partIndex: number) {
    super(`upload cancelled after part ${partIndex}`);
    this.name = "UploadCancelledError";
  }
}

/**
 * Thrown by `runUploadLoop` when `checkInterrupt` reports `"peer-error"` at
 * the per-part checkpoint — the RECEIVER sent a terminal `error` ctrl while
 * this loop was live. Distinct from `UploadCancelledError` because §8.2
 * gives the two different terminals: a peer cancel ends `"cancelled"`, a
 * peer error ends `"failed"` carrying the peer's own `code`.
 */
export class UploadPeerFailedError extends Error {
  constructor(public readonly partIndex: number) {
    super(`upload aborted: peer reported a terminal error at part ${partIndex}`);
    this.name = "UploadPeerFailedError";
  }
}

/**
 * Thrown by `runUploadLoop` when a `SenderSource.readChunks` yields a different
 * number of chunks than the requested range contains — a short/corrupt part the
 * receiver would reject (the CLI's 0-byte adapter under-yielded exactly this
 * way). Distinguishable rather than a plain Error, mirroring the sibling classes
 * above, so a caller's catch chain can treat it as a terminal sender-local
 * failure BY TYPE — structural, not by relying on a generic catch-all landing on
 * the right terminal.
 */
export class SourceUnderYieldError extends Error {
  constructor(
    public readonly partIndex: number,
    public readonly expected: number,
    public readonly got: number
  ) {
    super(`source under-yielded part ${partIndex}: expected ${expected} chunk(s), got ${got}`);
    this.name = "SourceUnderYieldError";
  }
}

export interface UploadLoopParams {
  api: Pick<TransferApi, "uploadUrl" | "putPart" | "markUploaded">;
  crypto: Pick<SenderCrypto, "encryptChunk">;
  /**
   * File-aligned global-part -> per-file chunk-range map (multi-file transfer design §4,
   * docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md). Indexed by `globalPart`
   * (`plan[globalPart].globalPart === globalPart`) — a single-file transfer's plan is the
   * degenerate one-file case, so this loop is byte-identical to the old chunkCount-only math.
   */
  plan: readonly PartPlanEntry[];
  /** Resolve file `fileIndex`'s read handle — never the original `File` (see `SenderSource.readChunks` doc). */
  sourceFor(fileIndex: number): Pick<SenderSource, "readChunks">;
  /** Each file's total chunk count, indexed by fileIndex — used to compute the file-LOCAL `isFinal` flag. */
  fileChunkCounts: readonly number[];
  transferKey: Uint8Array;
  noncePrefix: Uint8Array;
  transferId: string;
  partCount: number;
  /** Called after a part's PUT + `uploaded` both succeed — callers persist P-S2 here. */
  onPartUploaded(partIndex: number): Promise<void>;
  onProgress?(partIndex: number, partCount: number): void;
  /**
   * Cooperative pause/cancel checkpoint (controller resolution, TASK-19):
   * polled AFTER each part's `onPartUploaded` (P-S2) persist — never
   * mid-part, parts are atomic. Returns `"pause"`/`"cancel"` when the
   * caller's control registry (`activeSendControl.ts`) has a request
   * pending for this transfer, `undefined` otherwise. Optional — omitted in
   * tests/callers that don't exercise pause/cancel.
   */
  checkInterrupt?(): "pause" | "cancel" | "peer-error" | undefined;
}

function concatChunks(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function encryptPart(
  globalPart: number,
  params: UploadLoopParams,
  /** Per-file GLOBAL chunk offsets — computed ONCE per loop by `runUploadLoop` (see `globalChunkOffsets`). */
  chunkOffsets: readonly number[]
): Promise<Uint8Array> {
  const entry = params.plan[globalPart];
  const cipherChunks: Uint8Array[] = [];
  for await (const { index, data } of params
    .sourceFor(entry.fileIndex)
    .readChunks(entry.localChunkStart, entry.localChunkEndInclusive)) {
    // `index` is the file-LOCAL chunk index (0-based within entry.fileId). The two
    // indices below are deliberately DIFFERENT (crypto-spec.md §6/§6a):
    //   - AAD chunkIndex = file-LOCAL (`transferId|fileId|chunkIndex|isFinal`) — the
    //     receiver's contract, and `fileId` is what binds a chunk to its file.
    //   - NONCE counter = file-aligned GLOBAL. The transfer has ONE key and ONE
    //     `noncePrefix`, so a per-file counter would give chunk i of file A and chunk i
    //     of file B the same (key, nonce) — i.e. the same XChaCha20 keystream. The AAD
    //     only feeds the Poly1305 tag, so it cannot save us there.
    // For a single-file transfer global === local, so this stays byte-identical to the
    // pre-multi-file math (and to every deployed peer).
    const isFinal = index === params.fileChunkCounts[entry.fileIndex] - 1;
    const ciphertext = await params.crypto.encryptChunk(
      params.transferKey,
      params.noncePrefix,
      { transferId: params.transferId, fileId: entry.fileId },
      index,
      isFinal,
      data,
      chunkOffsets[entry.fileIndex] + index
    );
    cipherChunks.push(ciphertext);
  }
  // Every index in [localChunkStart, localChunkEndInclusive] is a real chunk (the range is
  // capped at chunkCount - 1, and a 0-byte file still has one empty chunk), so a
  // correct source yields EXACTLY this many. An adapter that under-yields (as the
  // CLI's did for a 0-byte file) would otherwise upload a short/empty part that
  // only the receiver rejects — a silent sender false-success. Fail loudly here,
  // at the sender, instead. Thrown OUTSIDE uploadPartWithRetry's per-attempt try,
  // so it surfaces as a terminal local failure, not a retried network error.
  // `max(0, …)`: an end < start range legitimately has zero chunks (never produced by
  // `partPlan`, but defensive since a fixture could still construct one).
  const expected = Math.max(0, entry.localChunkEndInclusive - entry.localChunkStart + 1);
  if (cipherChunks.length !== expected) {
    throw new SourceUnderYieldError(globalPart, expected, cipherChunks.length);
  }
  return concatChunks(cipherChunks);
}

/**
 * `ALREADY_COMPLETE`/`DEFINITIVE_404` (§4.4) are definitive signals, not
 * transient failures — docs/file-transfer-resume.md §5.2 step 5 has them
 * short-circuit ("re-GET" / "§7.2") rather than exhaust the retry budget.
 * Every other error (network, presigned, 5xx, …) keeps the M1 retry-then-fail
 * contract verbatim.
 */
async function uploadPartWithRetry(
  partIndex: number,
  body: Uint8Array,
  params: UploadLoopParams
): Promise<void> {
  const maxAttempts = PART_RETRY_BACKOFF_MS.length + 1;
  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await delay(PART_RETRY_BACKOFF_MS[attempt - 1]);
    }
    try {
      const { url } = await params.api.uploadUrl(params.transferId, partIndex);
      await params.api.putPart(url, body);
      await params.api.markUploaded(params.transferId, partIndex);
      return;
    } catch (err) {
      if (isAlreadyComplete(err) || isDefinitive404(err)) throw err;
      lastErr = err;
    }
  }
  throw new PartUploadError(partIndex, lastErr);
}

/**
 * Uploads every part in `uploadSetAscending` (must already be sorted
 * ascending by the caller — fresh send: `[skipBelowPart, partCount)`;
 * resume: the server-reconciled `uploadSet`, docs/file-transfer-resume.md
 * §5.2 step 3). Re-encrypts each part's plaintext freshly from `source` on
 * every call (including retries) — ciphertext is deterministic (crypto-spec
 * §1.2), so a re-PUT of any part is always safe.
 */
export async function runUploadLoop(
  uploadSetAscending: readonly number[],
  params: UploadLoopParams
): Promise<void> {
  // Derived once per loop from the manifest's file order — the nonce counter's
  // per-file base (crypto-spec.md §6a). Never persisted, never sent: the receiver
  // recomputes the identical array from the same manifest.
  const chunkOffsets = globalChunkOffsets(params.fileChunkCounts);
  for (const partIndex of uploadSetAscending) {
    params.onProgress?.(partIndex, params.partCount);
    const body = await encryptPart(partIndex, params, chunkOffsets);
    await uploadPartWithRetry(partIndex, body, params);
    await params.onPartUploaded(partIndex);
    // Checkpoint AFTER the part's persist (controller resolution) — parts
    // are atomic, so this is the only point a pause/cancel may take effect.
    const interrupt = params.checkInterrupt?.();
    if (interrupt === "pause") throw new UploadPausedError(partIndex);
    if (interrupt === "cancel") throw new UploadCancelledError(partIndex);
    if (interrupt === "peer-error") throw new UploadPeerFailedError(partIndex);
  }
}
